import Vue from 'vue'
import Vuex, {
  mapMutations,
  mapActions
} from 'vuex'

import router from '@/router'
import {
  deepCopy
} from '@/util'
import {
  sync
} from 'vuex-router-sync'
import data from '@/data'

// modules
import assignment from './modules/assignment'
import auth, {
  onAuthStateChange
} from './modules/auth'
import cell from './modules/cell'
import multiplication from './modules/multiplication'
import config from './modules/config'
import meeting from './modules/meeting'
import meeting2 from './modules/meeting2'
import persons from './modules/persons'
import root from './modules/root'
import stats2 from './modules/stats2'

Vue.use(Vuex)

const modules = {
  assignment,
  auth,
  cell,
  multiplication,
  config,
  meeting,
  meeting2,
  persons,
  stats2
}

// Why redux pattern here: https://medium.com/@dan_abramov/you-might-not-need-redux-be46360cf367

const store = new Vuex.Store(Object.assign({
  strict: process.env.NODE_ENV !== 'production',
  modules,
  //   plugins: debug ? [createLogger()] : []
}, root))

sync(store, router)

onAuthStateChange(store, router)

router.beforeEach((to, from, next) => {
  // If this route requires auth, check if signed in, if not, redirect to a signin page.
  const requiresAuth = !store.state.auth.user && to.matched.some(_ => _.meta.auth)
  // console.log('Before each ', requiresAuth, store.state.auth.user, from.fullPath, to.fullPath, next.fullPath)
  if (requiresAuth) {
    store.commit('redirect', {
      path: to.fullPath,
      roles: to.meta.roles
    })
    next('/signin')
  } else {
    next() // make sure to always call next()!
  }
})

function getProperty(o, path) {
  const tokens = path.split('/')
  var v = o
  for (let k of tokens) v = v[k]
  return v
}

/** Root can store.state or store.getters */
export function mapStateMutations(path) {
  // store is undefined here:
  // const tokens = key.split('/')
  // const last = tokens[tokens.length - 1]
  // let state = store.state
  // for (let i = 0; i < tokens.last; i++) state = state[i]
  return {
    get() {
      return getProperty(store.state, path)
    },
    set(value) {
      store.commit(path, value)
    }
  }
}

export {
  mapState,
  mapGetters,
  mapMutations,
  mapActions
}
from 'vuex'
export function mapGettersMutations(path) {
  // store is undefined here:
  // const tokens = key.split('/')
  // const last = tokens[tokens.length - 1]
  // let state = store.state
  // for (let i = 0; i < tokens.last; i++) state = state[i]
  return {
    get() {
      return store.getters[path]
    },
    set(value) {
      store.commit(path, value)
    }
  }
}

// export const vuexMixin = {
//   methods: {
//     mapAll (path) {
//       const store = this.$store
//       const filter = o => {
//         const o2 = {}
//         const re = new RegExp(`${path}/(.+$)`, 'g')
//         Object.keys(o).forEach(k => {
//           if (k.startsWith(path)) {
//             const v = o[k]
//             o2[k.replace(re, '$1')] = Array.isArray(v) ? v[0] : v
//           }
//         })
//         return o2
//       }
//       // console.log('filter', filter(store.getters))
//       Object.assign(this, getProperty(store.state, path))
//       Object.assign(this, filter(store.getters))
//       Object.assign(this, filter(store._mutations))
//       Object.assign(this, filter(store._actions))
//     }
//   }
// }

// Initialize all modules
Promise.all(Object.entries(modules)
  .map(([k, m]) => m.actions && Object.keys(m.actions).includes('init') ?
    store.dispatch(k + '/init', null, {
      root: true
    }) : Promise.resolve()))

// Create mutation for each state field ecept those already defined
export function defaultMutations(state, mutations) {
  mutations = mutations || {}
  const mutationKeys = Object.keys(mutations)
  Object.keys(state).forEach(k => {
    if (!mutationKeys.includes(k)) {
      mutations[k] = (state, payload) => {
        state[k] = payload
      }
    }
  })
  return mutations
}

export function mapAll(module, self) {
  self.computed = self.computed || {}
  self.methods = self.methods || {}
  const prefix = module + '/'

  // State
  Object.keys(store.state[module]).map(k => {
    if (self.computed[k]) throw new Error(`Computed property '${k} is already defined`)
    self.computed[k] = {
      get() {
        return store.state[module][k]
      },
      set(value) {
        store.commit(module + '/' + k, value)
      }
    }
  })

  // Getters
  Object.keys(store.getters).filter(path => path.startsWith(prefix)).map(path => {
    const k = path.substr(prefix.length)
    if (self.computed[k]) throw new Error(`Computed property '${k} is already defined`)
    self.computed[k] = {
      get() {
        return store.getters[path]
      },
      set(value) {
        store.commit(path, value)
      }
    }
  })

  // Mutations
  Object.keys(store._mutations).filter(path => path.startsWith(prefix)).map(path => {
    const k = path.substr(prefix.length)
    if (self.methods[k]) throw new Error(`Method ${k} matching mutation '${path} is already defined`)
    // If there is no mutation included in the computed map
    if (!Object.keys(self.computed).includes(k)) {
      Object.assign(self.methods, mapMutations(module, [k]))
    }
  })

  // Actions
  Object.keys(store._actions).filter(path => path.startsWith(prefix)).map(path => {
    const k = path.substr(prefix.length)
    if (self.methods[k]) throw new Error(`Method ${k} matching action '${path} is already defined`)
    Object.assign(self.methods, mapActions(module, [k]))
  })

  return self
}

export function error(context) {
  return err => context.commit('error', err, {
    root: true
  })
}

// For testing
const initialState = deepCopy(store.state)
export function clearState() {
  data.clear()
  store.replaceState(deepCopy(initialState))
};

// export function dispath (action, payload) {
//    await store.dispath(action, payload)
// }

export default store
